import { useMemo, useContext, useEffect } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import type {
	useEnableEndUserRequestUpgradeQuery$data,
	useEnableEndUserRequestUpgradeQuery$variables,
} from './__generated__/useEnableEndUserRequestUpgradeQuery.graphql.ts';
import { getEligibleTouchpointForEdition } from './touchpoints.ts';
import { editionAnalytics } from './analytics/editionAnalytics.ts';

export const useEnableEndUserRequestUpgrade = (
	source: string,
): {
	showRequestUpgradeButton: boolean;
	loading: boolean;
} => {
	// TODO: During productionization replace with EligibleTouchpoints type for source
	const { cloudId, edition, userId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const eligibleTouchpoint = getEligibleTouchpointForEdition(source, edition);

	const offeringName = eligibleTouchpoint?.requestedEdition ?? 'standard';

	const isEnabledStandardEdition = edition === ConfluenceEdition.STANDARD;

	const { loading, data } = useQueryInterop<
		useEnableEndUserRequestUpgradeQuery$data,
		useEnableEndUserRequestUpgradeQuery$variables
	>(
		graphql`
			query useEnableEndUserRequestUpgradeQuery(
				$cloudId: ID!
				$offeringName: String!
				$shouldCheckForTrial: Boolean!
			) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlementId
						entitlement {
							id
							__typename
							experienceCapabilities {
								changeOfferingV2(offeringName: $offeringName) {
									isAvailableToUser
									experienceUrl
								}
							}

							transactionAccount {
								key
								isBillToPresent
							}

							relatesFromEntitlements {
								entitlementId
								relationshipType
							}
							preDunning @include(if: $shouldCheckForTrial) {
								status
							}

							subscription @include(if: $shouldCheckForTrial) {
								trial {
									timeLeft
								}
								accountDetails {
									invoiceGroup {
										invoiceable
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				cloudId,
				offeringName,
				shouldCheckForTrial: isEnabledStandardEdition,
			},
			fetchPolicy: 'cache-first',
			skip: !isEnabledStandardEdition || isSiteAdmin || !eligibleTouchpoint,
			interopApolloAggClient: true,
		},
	);

	const { isEligibleForEndUserRequestStandard } = useMemo(() => {
		const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
		const isInCollection = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'COLLECTION',
		)?.entitlementId;
		const isChangeOfferingAvailable =
			entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
		const changeOfferingExperienceUrl =
			entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;
		const canUpgrade = Boolean(isChangeOfferingAvailable && changeOfferingExperienceUrl);
		const hasPaymentDetailsEntered = Boolean(entitlement?.transactionAccount?.isBillToPresent);
		const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;
		const entitlementId = entitlementInfo?.entitlementId;
		const transactionAccountId = entitlement?.transactionAccount?.key;
		// Additional eligibility checks for Standard
		const trialTimeLeft = entitlement?.subscription?.trial?.timeLeft;
		const isInTrial = Boolean(trialTimeLeft && trialTimeLeft > 0);
		const invoiceableStatus =
			typeof entitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable === 'boolean'
				? entitlement.subscription.accountDetails.invoiceGroup.invoiceable
					? 'INVOICEABLE'
					: 'NOT_INVOICEABLE'
				: 'UNKNOWN';
		const isInPredunning =
			entitlement?.preDunning?.status === 'IN_PRE_DUNNING' &&
			invoiceableStatus === 'NOT_INVOICEABLE';

		return {
			isEligibleForEndUserRequestStandard: Boolean(
				!loading &&
				!isInCollection &&
				isEnabledStandardEdition &&
				hasPaymentDetailsEntered &&
				!canUpgrade &&
				Boolean(userId) &&
				Boolean(changeOfferingExperienceUrl) &&
				Boolean(transactionAccountId) &&
				Boolean(entitlementId) &&
				!isInTrial &&
				!isInPredunning,
			),
		};
	}, [data, loading, isEnabledStandardEdition, userId]);

	useEffect(() => {
		if (isEligibleForEndUserRequestStandard) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'feature',
					source: 'useEnableEndUserRequestUpgrade',
					attributes: {
						cohort: expVal('twcg-1169_ceurs2p_end_user_requests', 'cohort', 'not-enrolled'),
						flagKey: 'twcg-1169_ceurs2p_end_user_requests',
						touchpoint: source,
						...editionAnalytics(edition),
					},
				},
			}).fire();
		}
	}, [edition, isEligibleForEndUserRequestStandard, createAnalyticsEvent, source]);

	return {
		showRequestUpgradeButton:
			// Standard Edition
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			isEligibleForEndUserRequestStandard &&
			expValEquals('twcg-1169_ceurs2p_end_user_requests', 'cohort', 'variation'),
		loading,
	};
};
